import React from "react";
import { ChevronRight, Ban, Wrench, Bell, FileQuestion, RefreshCw, Hand } from "lucide-react";

const TYPE_META = {
  negotiate:                { Icon: Hand,         label: "Negotiate" },
  negotiation:              { Icon: Hand,         label: "Negotiate" },
  renegotiate_specific:     { Icon: Hand,         label: "Renegotiate term" },
  act_now:                  { Icon: Wrench,       label: "Act now" },
  accept_with_terms:        { Icon: Wrench,       label: "Accept w/ terms" },
  monitor:                  { Icon: Bell,         label: "Monitor" },
  decline:                  { Icon: Ban,          label: "Decline" },
  decline_to_sign:          { Icon: Ban,          label: "Decline to sign" },
  request_documentation:    { Icon: FileQuestion, label: "Request docs" },
  request_clarification:    { Icon: FileQuestion, label: "Request clarification" },
  renegotiate_before_renewal: { Icon: RefreshCw,  label: "Renegotiate before renewal" },
};

const EFFORT_TONE = {
  Low:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  High:   "bg-rose-50 text-rose-700 border-rose-200",
};

function priorityTone(p) {
  if (p <= 1) return "bg-red-600 text-white";
  if (p === 2) return "bg-rose-500 text-white";
  if (p === 3) return "bg-amber-500 text-white";
  return "bg-zinc-300 text-zinc-700";
}

/**
 * Typed recommendation card — strict schema:
 *  priority, type, action, rationale, expected_impact, effort,
 *  deadline?, blocks_signing, source_finding_ids/risk_paths/change_ids/doc_ids.
 */
export default function RecommendationCard({ rec, index }) {
  const meta = TYPE_META[rec.type] || { Icon: ChevronRight, label: rec.type };
  const Icon = meta.Icon;
  const sources = [
    ...(rec.source_finding_ids || []).map((id) => ({ kind: "finding", id })),
    ...(rec.source_risk_paths || []).map((p) => ({ kind: "risk", id: p })),
    ...(rec.source_change_ids || []).map((id) => ({ kind: "change", id })),
    ...(rec.source_doc_ids || []).map((id) => ({ kind: "doc", id })),
    ...(rec.source_criterion_keys || []).map((id) => ({ kind: "criterion", id })),
  ];

  return (
    <article
      data-testid={`recommendation-card-${index}`}
      className={`relative border rounded-xl p-4 bg-white hover:shadow-md transition-shadow ${
        rec.blocks_signing ? "border-red-300 ring-1 ring-red-100" : "border-zinc-200"
      }`}
    >
      <header className="flex items-start gap-3 mb-2.5">
        <span
          className={`shrink-0 w-7 h-7 rounded-md grid place-items-center font-display font-semibold tabular-nums text-xs ${priorityTone(rec.priority)}`}
          data-testid={`recommendation-priority-${index}`}
          title={`Priority ${rec.priority}`}
        >
          {rec.priority}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Icon size={13} className="text-zinc-500 shrink-0" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{meta.label}</span>
            {rec.effort && (
              <span
                data-testid={`recommendation-effort-${index}`}
                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold ${EFFORT_TONE[rec.effort] || EFFORT_TONE.Medium}`}
              >
                Effort: {rec.effort}
              </span>
            )}
            {rec.blocks_signing && (
              <span
                data-testid={`recommendation-blocks-${index}`}
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-semibold"
              >
                Blocks signing
              </span>
            )}
            {rec.deadline && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-50 text-zinc-700 border border-zinc-200 font-medium">
                Due {rec.deadline}
              </span>
            )}
          </div>
          <p
            className="text-sm font-semibold text-zinc-950 leading-snug"
            data-testid={`recommendation-action-${index}`}
          >
            {rec.action}
          </p>
        </div>
      </header>
      {rec.rationale && (
        <p className="text-sm text-zinc-700 leading-relaxed pl-10" data-testid={`recommendation-rationale-${index}`}>
          {rec.rationale}
        </p>
      )}
      {rec.expected_impact && (
        <p className="text-xs text-zinc-500 mt-1.5 pl-10" data-testid={`recommendation-impact-${index}`}>
          <span className="font-semibold uppercase tracking-wider text-[10px] text-zinc-400">Expected impact · </span>
          {rec.expected_impact}
        </p>
      )}
      {sources.length > 0 && (
        <div className="mt-3 pl-10 flex flex-wrap gap-1.5" data-testid={`recommendation-sources-${index}`}>
          {sources.slice(0, 5).map((s, i) => (
            <span
              key={`${s.kind}-${i}`}
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-medium"
              title={s.id}
            >
              {s.kind}: {String(s.id).slice(0, 8)}
            </span>
          ))}
          {sources.length > 5 && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-medium">
              +{sources.length - 5} more
            </span>
          )}
        </div>
      )}
    </article>
  );
}
